import {Injectable} from '@angular/core';

import {
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    CanLoad,
    CanActivate,
    CanActivateChild,
    Route,
    UrlSegment
} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    AuthApiService
} from '@spa/api';

import {CacheCollectionService} from './cache-collection.service';

import {ErrorMessageIdSelector} from '../../../assets/descriptors';

import {CommonMessages} from '../../../assets/messages/common.messages';

@Injectable()
export class AuthGuardService implements CanActivate, CanActivateChild, CanLoad {
    constructor(
        private authenticationService: AuthApiService,
        protected router: Router,
        protected cacheCollectionService: CacheCollectionService
    ) {
    }

    private doCheck(): Observable<boolean> {
        if (!this.authenticationService.getAwaitLogin()) {
            return this.authenticationService
                .isUserAuthenticated()
                .pipe(
                    map(this.mapHandler.bind(this))
                );
        }

        return this.authenticationService
            .completeLoginRedirect()
            .pipe(
                map(this.mapHandler.bind(this))
            );
    }

    private mapHandler(response: ApiResponse<ApiError[] | boolean>): boolean {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            let messages = CommonMessages.InternalServerError;

            const timestamp = moment().unix();

            if (response.status === HttpStatusCodesEnum.Unauthorized) {
                messages = CommonMessages.UnauthorizedError;
            }else  if (response.status === HttpStatusCodesEnum.Forbidden) {
                messages = CommonMessages.ForbiddenError;
            }

            this.cacheCollectionService.addItem(timestamp.toString(), messages);

            this.router.navigate(['/error'], {
                queryParams: {
                    [ErrorMessageIdSelector]: timestamp
                }
            }).finally();


            return false;
        }

        const userCheck = response.data as boolean;

        if (!userCheck) {
            this.router.navigate(['/authentication', 'login'])
                .finally();

            return false;
        }

        return true;
    }

    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
        return this.doCheck();
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck();
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck();
    }
}
